// Settings › Loyalty program — settings/loyalty (moved from marketing/loyaltyManagement.js).
// Global earning / redemption parameters plus per-service overrides.
import Switch from 'rc-switch';
import 'rc-switch/assets/index.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { Button, Col, Input, Row } from 'reactstrap';
import { firestore } from '../../../helpers/Firebase';
import { createNotification } from '../../../helpers/Utils';
import { Field, SettingsCard, StatusPill } from './SettingsLayout';

const DEFAULTS = { active: false, percent: 0, capPercent: 0, capValue: 0, expiration: 90, services: {} };

const LoyaltySettings = () => {
  const { services: reduxServices } = useSelector((state) => state.providers);
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    firestore().collection('settings').doc('loyalty').get().then((snap) => {
      if (snap.exists) {
        const d = snap.data();
        setSettings({
          active: d.active === true,
          percent: Number(d.percent) || 0,
          capPercent: Number(d.capPercent) || 0,
          capValue: Number(d.capValue) || 0,
          expiration: Number(d.expiration) || 90,
          services: d.services || {},
        });
        setUpdatedAt(d.updatedAt || null);
      }
    }).catch((error) => { console.error('Error loading loyalty settings:', error); createNotification('error', 'Failed to load loyalty settings', 'Settings'); })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = useCallback((field, value) => { setSettings((prev) => ({ ...prev, [field]: value })); setDirty(true); }, []);

  const servicesList = useMemo(() => {
    if (!reduxServices) return [];
    return Object.values(reduxServices).filter((s) => !s.deActivated).map((s) => ({ key: s.key, name: s.name }));
  }, [reduxServices]);

  const handleAddServiceOverride = useCallback((serviceId) => {
    if (!serviceId || settings.services[serviceId]) return;
    const service = servicesList.find((s) => s.key === serviceId);
    setSettings((prev) => ({
      ...prev,
      services: {
        ...prev.services,
        [serviceId]: { serviceName: service?.name || '', percent: prev.percent, capPercent: prev.capPercent, capValue: prev.capValue, expiration: prev.expiration },
      },
    }));
    setDirty(true);
  }, [settings.services, servicesList]);

  const handleServiceOverrideChange = useCallback((serviceId, field, value) => {
    setSettings((prev) => ({ ...prev, services: { ...prev.services, [serviceId]: { ...prev.services[serviceId], [field]: value } } }));
    setDirty(true);
  }, []);

  const handleRemoveServiceOverride = useCallback((serviceId) => {
    setSettings((prev) => { const updated = { ...prev.services }; delete updated[serviceId]; return { ...prev, services: updated }; });
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        active: settings.active,
        percent: Number(settings.percent) || 0,
        capPercent: Number(settings.capPercent) || 0,
        capValue: Number(settings.capValue) || 0,
        expiration: Number(settings.expiration) || 90,
        services: Object.entries(settings.services || {}).reduce((acc, [id, svc]) => {
          acc[id] = {
            serviceName: svc.serviceName || '',
            percent: Number(svc.percent) || 0,
            capPercent: Number(svc.capPercent) || 0,
            capValue: Number(svc.capValue) || 0,
            expiration: Number(svc.expiration) || 90,
          };
          return acc;
        }, {}),
        updatedAt: Date.now(),
      };
      // `services` is replaced as a whole so removed overrides really go away.
      await firestore().collection('settings').doc('loyalty').set(payload, { merge: true });
      setUpdatedAt(payload.updatedAt);
      setDirty(false);
      createNotification('success', 'Loyalty settings saved successfully', 'Settings');
    } catch (error) {
      console.error('Error saving loyalty settings:', error);
      createNotification('error', 'Failed to save loyalty settings', 'Settings');
    } finally { setSaving(false); }
  }, [settings]);

  if (loading) return <div className="loading" />;

  const footer = (
    <>
      <span className="st-foot-note">{updatedAt ? `Last saved ${new Date(Number(updatedAt)).toLocaleString()}` : ''}{dirty ? ' · unsaved changes' : ''}</span>
      <Button color="primary" disabled={saving || !dirty} onClick={handleSave}>{saving ? 'Saving…' : 'Save changes'}</Button>
    </>
  );

  return (
    <SettingsCard
      title="Loyalty program"
      description="Customers earn a percentage of each booking as points and redeem them on later orders."
      status={<StatusPill on={settings.active} onLabel="Active" offLabel="Disabled" />}
      footer={footer}
    >
      <div className="st-section">
        <div className="st-section-title">Global rules</div>
        <div className="st-switch-row">
          <div className="st-switch-text">
            <b>Loyalty program {settings.active ? 'is active' : 'is disabled'}</b>
            <span>While disabled, no points are earned or redeemed.</span>
          </div>
          <Switch className="custom-switch custom-switch-primary" checked={settings.active} onChange={(checked) => handleChange('active', checked)} />
        </div>
        <Row>
          <Col md="3">
            <Field label="Earning percentage (%)" help="Share of the booking total earned as points.">
              <Input type="number" min="0" max="100" step="0.5" value={settings.percent} onChange={(e) => handleChange('percent', e.target.value)} />
            </Field>
          </Col>
          <Col md="3">
            <Field label="Redemption cap (%)" help="Max share of an order payable with points.">
              <Input type="number" min="0" max="100" step="1" value={settings.capPercent} onChange={(e) => handleChange('capPercent', e.target.value)} />
            </Field>
          </Col>
          <Col md="3">
            <Field label="Max discount per service (AED)" help="Max AED discount per booking when redeeming.">
              <Input type="number" min="0" step="1" value={settings.capValue} onChange={(e) => handleChange('capValue', e.target.value)} />
            </Field>
          </Col>
          <Col md="3">
            <Field label="Expiration (days)" help="Days before earned points expire.">
              <Input type="number" min="1" step="1" value={settings.expiration} onChange={(e) => handleChange('expiration', e.target.value)} />
            </Field>
          </Col>
        </Row>
      </div>

      <div className="st-section">
        <div className="st-section-title">Per-service overrides</div>
        <Row className="align-items-end">
          <Col md="6">
            <Field label="Add a service" help="Overrides the global rules for one service.">
              <Select
                placeholder="Choose a service…"
                value={null}
                options={servicesList.filter((s) => !settings.services[s.key]).map((s) => ({ value: s.key, label: s.name }))}
                onChange={(opt) => opt && handleAddServiceOverride(opt.value)}
                isClearable={false}
                styles={{ control: (base) => ({ ...base, borderRadius: 8, fontSize: '0.85rem' }), menu: (base) => ({ ...base, fontSize: '0.85rem', zIndex: 9999 }) }}
              />
            </Field>
          </Col>
        </Row>
        {Object.keys(settings.services).length === 0 ? (
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>No per-service overrides. All services use the global rules above.</p>
        ) : Object.entries(settings.services).map(([serviceId, override]) => (
          <div key={serviceId} className="st-override">
            <div className="st-override-head">
              <span>{override.serviceName || serviceId}</span>
              <Button color="danger" size="xs" outline onClick={() => handleRemoveServiceOverride(serviceId)}>Remove</Button>
            </div>
            <Row>
              {[['percent', 'Earn %', { min: 0, max: 100, step: 0.5 }], ['capPercent', 'Cap %', { min: 0, max: 100, step: 1 }], ['capValue', 'Max AED', { min: 0, step: 1 }], ['expiration', 'Expiry (days)', { min: 1, step: 1 }]].map(([field, label, attrs]) => (
                <Col key={field} xs="6" md="3">
                  <Field label={label}>
                    <Input type="number" bsSize="sm" {...attrs} value={override[field]} onChange={(e) => handleServiceOverrideChange(serviceId, field, e.target.value)} />
                  </Field>
                </Col>
              ))}
            </Row>
          </div>
        ))}
      </div>
    </SettingsCard>
  );
};

export default LoyaltySettings;
